import { ActionIcon, NavLink, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconLayoutSidebarRightCollapseFilled,
  IconLayoutSidebarRightExpandFilled,
  IconTrash,
} from '@tabler/icons-react';
import { Link, Outlet, useMatches } from '@tanstack/react-router';
import cx from 'clsx';
import { motion } from 'motion/react';
import type { ReactNode } from 'react';

import classes from './Bookmarks.module.css';
import type { Bookmark } from './types.ts';

export interface BookmarksProps<BookmarkType extends Bookmark> {
  title?: ReactNode;
  bookmarks: BookmarkType[];
  onDelete?: (bookmark: BookmarkType) => void;
  groupBy?: (bookmark: BookmarkType) => string;
  /** Returns route params for navigating to a bookmark. Defaults to `{ id: bookmark.id }`. */
  getParams?: (bookmark: BookmarkType) => Record<string, string>;
}

export function Bookmarks<
  BookmarkDataType = Record<string, unknown>,
  BookmarkType extends Bookmark<BookmarkDataType> = Bookmark<BookmarkDataType>,
>({ title, bookmarks, onDelete, groupBy, getParams }: React.PropsWithChildren<BookmarksProps<BookmarkType>>) {
  const matches = useMatches();
  const groupedBookmarks = groupBy
    ? bookmarks.reduce((groups, bookmark) => {
        const key = groupBy(bookmark);
        const group = groups.get(key) ?? [];
        group.push(bookmark);
        groups.set(key, group);
        return groups;
      }, new Map<string, BookmarkType[]>())
    : new Map([['', bookmarks]]);

  const [isSidebarVisible, { toggle }] = useDisclosure(true);

  return (
    <motion.div
      animate={{ '--sidebar-width-factor': isSidebarVisible ? 1 : 0 }}
      className={cx(classes.container, isSidebarVisible && (classes.visible ?? 'visible'))}
      initial={{ '--sidebar-width-factor': 1 }}
      layout
    >
      <div className={classes.content}>
        <Outlet />
      </div>

      <div className={classes.sidebar}>
        <header>
          {title && <div className={classes.title}>{title}</div>}

          <div className={classes.toggleSidebar}>
            <Tooltip label={`${isSidebarVisible ? 'Hide' : 'Show'} Sidebar`} position="left" withArrow>
              <ActionIcon onClick={toggle} size="md" variant="subtle">
                {isSidebarVisible ? (
                  <IconLayoutSidebarRightCollapseFilled size={24} />
                ) : (
                  <IconLayoutSidebarRightExpandFilled size={24} />
                )}
              </ActionIcon>
            </Tooltip>
          </div>
        </header>
        <div className={classes.bookmarks}>
          {[...groupedBookmarks.entries()].map(([category, items]) => (
            <div className={classes.group} key={category}>
              {category && <div className={classes.groupTitle}>{category}</div>}

              {items.map((bookmark) => (
                <div className={classes.bookmark} key={bookmark.id}>
                  <div className={classes.bookmarkLink}>
                    <NavLink
                      component={Link}
                      from={
                        // FIXME: assumes 2nd level routes determine the base path
                        matches.at(1)?.pathname
                      }
                      label={bookmark.title}
                      leftSection={bookmark.icon}
                      params={
                        // biome-ignore lint/suspicious/noExplicitAny: TanStack Router requires params typed for specific routes, but dynamic $id routes can't be statically typed
                        (getParams?.(bookmark) ?? { id: bookmark.id }) as any
                      }
                      to="$id"
                      variant="filled"
                    />
                  </div>

                  {onDelete && (
                    <div className={classes.deleteButton}>
                      <ActionIcon
                        color="red"
                        onClick={(event: React.MouseEvent) => {
                          event.preventDefault();
                          event.stopPropagation();
                          onDelete?.(bookmark);
                        }}
                        variant="transparent"
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}
